import {
    AfterViewInit,
    Component,
    computed,
    ElementRef,
    HostListener,
    inject,
    OnInit,
    Renderer2,
    signal,
    viewChild,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { environment } from '@env/environment';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { AppConfig } from './app.config';
import { EventsService } from './core/services/event.service';

import { themeState } from './core/stores/themeStore';
import { IdleDialogComponent } from './shared/components/idle-dialog/idle-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MaintenanceService } from './shared/services/maintenance.service';
import { DebugService } from './shared/services/debug.service';
import { SessionService } from './core/services/client-services/session-service/session.service';
import { AuthService } from '@app/core/services/auth.service';
import { AppStore } from '@app/stores/appStore';
import { toHtmlLang } from '@app/shared/utils/html-lang';
import { HeaderComponent } from './core/components/header/header.component';
import { GlobalFooterComponent } from './core/components/global-footer/global-footer.component';
import { SelfserviceFooterComponent } from './core/components/selfservice-footer/selfservice-footer.component';
import { NgxSpinnerComponent } from 'ngx-spinner';
import { DebugToggleComponent } from './shared/components/debug-command-window/debug-toggle.component';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [
        HeaderComponent,
        RouterOutlet,
        GlobalFooterComponent,
        SelfserviceFooterComponent,
        NgxSpinnerComponent,
        DebugToggleComponent,
    ],
})
export class AppComponent implements OnInit, AfterViewInit {
    private appStore = inject(AppStore);
    showHeader = signal(true);
    isSelfService = signal(false);
    isManagementView = signal(false);
    contentArea = viewChild<ElementRef>('contentArea');
    isFullscreen = computed(() => themeState().isFullscreen);
    get maintenanceMode(): boolean {
        return this.appStore.maintenanceMode();
    }

    isProduction = signal(false);
    currentEnvironment: 'production' | 'test';
    environment = environment;
    isLoggedIn = signal(false);
    private retryCount = 0;
    private maxRetries = 100;
    private retryDelay = 300;

    constructor(
        private renderer: Renderer2,
        private translate: TranslateService,
        private router: Router,
        private appConfig: AppConfig,
        private eventsService: EventsService,
        private dialog: MatDialog,
        // Injected to ensure service is instantiated (starts maintenance polling)
        private maintenanceService: MaintenanceService,
        private debugService: DebugService,
        private sessionService: SessionService,
        private authService: AuthService
    ) {}

    async ngOnInit(): Promise<void> {
        await this.authService.configureOAuthService();

        this.isLoggedIn.set(await this.sessionService.isLoggedIn());
        this.isProduction.set(this.appConfig.production || false);
        this.debugService
            .getEnvironment()
            .pipe(untilDestroyed(this))
            .subscribe(env => {
                this.currentEnvironment = env;
                this.log(`Detected environment state: ${env}`);
            });

        this.debugService
            .getLoggingState()
            .pipe(untilDestroyed(this))
            .subscribe(enabled => {
                this.log(
                    `Verbose logging state: ${enabled ? 'enabled' : 'disabled'}`
                );
            });

        if (
            'GestureEvent' in window &&
            !('touchAction' in document.documentElement.style)
        ) {
            document.documentElement.addEventListener(
                'gesturestart',
                (e: Event) => {
                    e.preventDefault();
                },
                { passive: false, capture: true }
            );
        }

        this.eventsService
            .subscribe('user-idle:show')
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                const dialogRef = this.dialog.open(IdleDialogComponent, {
                    width: '500px',
                    disableClose: true,
                });
                dialogRef.afterClosed().subscribe(result => {
                    if (result) {
                        location.reload();
                    }
                });
            });

        this.isManagementView.set(this.router.url.startsWith('/management'));
        this.router.events.pipe(untilDestroyed(this)).subscribe(event => {
            if (event instanceof NavigationEnd) {
                const showNoHeader = [
                    '/forgot-password',
                    '/check-email',
                    '/set-new-password',
                    '/maintenance',
                    '/external/reset-user-password',
                    '/selfservice',
                    '/management',
                    // Diese Seiten bringen ihr eigenes Vollbild-Layout mit
                    // und decken den Header vollständig ab. Bliebe er im DOM,
                    // wäre sein Logo-Link per Tastatur erreichbar, aber nie
                    // anklickbar — eine Fokus-Falle.
                    '/login',
                    '/login-rate-limit',
                    '/login-callback',
                ];

                this.showHeader.set(
                    !showNoHeader.includes(event.urlAfterRedirects)
                );
                this.isSelfService.set(
                    event.urlAfterRedirects.startsWith('/selfservice')
                );
                this.isManagementView.set(
                    event.urlAfterRedirects.startsWith('/management')
                );
                if (!this.isSelfService()) {
                    sessionStorage.removeItem('reloadRoute');
                }
                const reloadRoute = sessionStorage.getItem('reloadRoute');
                const currentUrl = this.router.url;
                if (reloadRoute) {
                    sessionStorage.removeItem('reloadRoute');
                    if (
                        reloadRoute.startsWith('/selfservice') &&
                        currentUrl.startsWith('/selfservice')
                    ) {
                        this.router.navigateByUrl('/selfservice');
                        this.retryAdjustContentHeight();
                    }
                }
            }
        });

        this.translate.onLangChange
            .pipe(untilDestroyed(this))
            .subscribe(({ lang }) => this.setDocumentLanguage(lang));

        this.translate.setDefaultLang(environment.defaultLang);
        this.translate.use(environment.defaultLang);

        // use() bricht ohne Ereignis ab, wenn die Sprache bereits aktiv ist —
        // und der loadTranslations-Initializer hat 'de' schon gesetzt, bevor
        // diese Komponente existiert. Den Startwert daher explizit übernehmen.
        this.setDocumentLanguage(this.translate.currentLang);
    }

    /** Hält <html lang> auf der tatsächlich gerenderten Sprache. Ein
     *  widersprüchliches lang ist der klassische Auslöser dafür, dass Browser
     *  eine Übersetzung anbieten; außerdem hängen Screenreader-Aussprache und
     *  Silbentrennung daran. */
    private setDocumentLanguage(lang: string): void {
        this.renderer.setAttribute(
            document.documentElement,
            'lang',
            toHtmlLang(lang)
        );
    }

    ngAfterViewInit() {
        if (this.isSelfService()) {
            this.adjustContentHeight();
        }
    }

    @HostListener('window:resize')
    onResize() {
        if (this.isSelfService()) {
            this.adjustContentHeight();
        }
    }

    @HostListener('window:beforeunload')
    onBeforeUnload() {
        const currentUrl = this.router.url;
        if (currentUrl.startsWith('/selfservice')) {
            sessionStorage.setItem('reloadRoute', currentUrl);
        } else {
            sessionStorage.removeItem('reloadRoute');
        }
    }

    private adjustContentHeight() {
        const windowHeight = window.innerHeight;
        const adjustedHeight = windowHeight;
        const contentArea = this.contentArea();
        if (contentArea?.nativeElement) {
            this.renderer.setStyle(
                contentArea.nativeElement,
                'height',
                `${adjustedHeight}px`
            );
        }
    }

    private retryAdjustContentHeight() {
        if (this.isSelfService()) {
            if (this.retryCount < this.maxRetries) {
                setTimeout(() => {
                    if (
                        this.contentArea() &&
                        this.contentArea()!.nativeElement
                    ) {
                        this.adjustContentHeight();
                    } else {
                        this.retryCount++;
                        this.retryAdjustContentHeight();
                    }
                }, this.retryDelay);
            }
        }
    }

    private log(message: string): void {
        if (this.debugService.isLoggingEnabled()) {
            console.log(`[AppComponent] ${message}`);
        }
    }
}
